// utils/units/normalize.js
//
// The write-path coercer (`normalizeMeasurement`, strict: throws coded
// errors) and the read-path shape (`readMeasurement`, tolerant: never throws)
// for a measurement element. Dependency-free and vendored into keepr-web (see
// registry.js for the rule).
//
// Element options consulted: { measure, measures, ownUnits, defaultUnit, units, min, max }.
//
// An element's OWN units (KPR-1109, `ownUnits`) are measures of one unit
// each: a value in one is { value, unit: <its id>, base: value }, never
// negative, always allowed (the `units` allowlist lists built-in units).
// A measure with allowZero:false (fuel economy) refuses a zero as 'range'.
// Error codes: 'type' | 'invalid_unit' | 'range' (the row-validator vocabulary).
//
// An element that allows SEVERAL measures (KPR-1085, `measures`) reads and
// writes each value under the single-measure rules of ITS unit's measure
// (registry.js scopeOptions): the shape stays { value, unit, base }, `base`
// in that measure's canonical unit.

'use strict';

const { getMeasure, getUnit, isMeasurementValue, isMultiMeasure, measureOfUnit, scopeOptions, isOwnUnitId, ownUnitOf } = require('./registry');
const { toBase } = require('./convert');
const { parseMeasurement, parseMeasurementDetailed } = require('./parse');

// `reason`: the part of a parse refusal a caller with its own wording keeps
// (a malformed fraction's "a fraction cannot have 0 under the line").
function fail(code, message, reason) {
	const err = new Error(message);
	err.code = code;
	if (reason) err.reason = reason;
	return err;
}

function isEmpty(raw) {
	return raw === undefined || raw === null || raw === '' || (typeof raw === 'string' && raw.trim() === '');
}

function defaultUnitId(measure, options) {
	const unit = options && typeof options.defaultUnit === 'string' ? getUnit(options.defaultUnit) : null;
	if (unit && unit.measure === measure.id) return unit.id;
	return measure.canonical;
}

function boundInBase(bound, unitId) {
	if (typeof bound !== 'number' || !Number.isFinite(bound)) return null;
	return toBase(bound, unitId);
}

/**
 * normalizeMeasurement(raw, options) -> { value, unit, base } | null
 *
 * Null for empty input ('' / null / undefined). Throws an Error with `.code`
 * for anything that cannot be stored: not a measurement ('type'), a unit that
 * is not of the measure or not in the element's allowlist ('invalid_unit'),
 * a negative value on a measure that forbids it or a value outside
 * min/max ('range'). `base` is always recomputed here, never trusted.
 */
function normalizeMeasurement(raw, options) {
	if (isEmpty(raw)) return null;
	const opts = options || {};
	if (isMultiMeasure(opts)) return normalizeSeveral(raw, opts);
	return normalizeIn(raw, opts);
}

/**
 * A multi-measure element (KPR-1085): the reading names the measure (its
 * unit's), the element's allowlist is asked as a whole, and the value is then
 * normalised exactly as a single-measure element of that measure would --
 * min and max, written in the primary's default unit, reach the primary
 * measure only (registry.js scopeOptions).
 */
function normalizeSeveral(raw, opts) {
	const parsed = parseMeasurementDetailed(raw, opts);
	if (parsed.error) throw fail(parsed.error, parsed.message, parsed.reason);
	const scoped = scopeOptions(opts, measureOfUnit(parsed.unit), parsed.unit);
	if (!scoped) throw fail('invalid_unit', 'unit "' + parsed.unit + '" is not allowed for this element');
	// The allowlist lists built-in units; an element's own units are always
	// allowed (KPR-1109).
	const allowlist = Array.isArray(opts.units) ? opts.units : [];
	if (!isOwnUnitId(parsed.unit) && allowlist.length && allowlist.indexOf(parsed.unit) === -1) {
		throw fail('invalid_unit', 'unit "' + parsed.unit + '" is not allowed for this element');
	}
	return normalizeIn({ value: parsed.value, unit: parsed.unit }, scoped);
}

/**
 * One OWN unit's coercer (KPR-1109; the options registry.js scopeOptions
 * gives that unit): it counts, so a number that is not negative, and its base
 * is the number itself -- nothing converts to or from it.
 */
function normalizeOwn(raw, opts) {
	const own = ownUnitOf(opts.measure, opts);
	if (!own) throw fail('type', 'unknown measure "' + opts.measure + '"');
	const parsed = parseMeasurementDetailed(raw, opts);
	if (parsed.error) throw fail(parsed.error, parsed.message, parsed.reason);
	if (parsed.unit !== own.id) throw fail('invalid_unit', 'unit "' + parsed.unit + '" is not ' + own.plural);
	if (parsed.value < 0) throw fail('range', own.plural + ' cannot be negative');
	return { value: parsed.value, unit: own.id, base: parsed.value };
}

/** The single-measure coercer (the whole of it before KPR-1085). */
function normalizeIn(raw, opts) {
	if (isOwnUnitId(opts.measure)) return normalizeOwn(raw, opts);
	const measure = getMeasure(opts.measure);
	if (!measure) throw fail('type', 'unknown measure "' + opts.measure + '"');

	const parsed = parseMeasurementDetailed(raw, opts);
	if (parsed.error) throw fail(parsed.error, parsed.message, parsed.reason);

	const unit = getUnit(parsed.unit);
	if (!unit || unit.measure !== measure.id) {
		throw fail('invalid_unit', 'unit "' + parsed.unit + '" is not a ' + measure.id + ' unit');
	}
	const allowlist = Array.isArray(opts.units) ? opts.units : [];
	if (allowlist.length && allowlist.indexOf(unit.id) === -1) {
		throw fail('invalid_unit', 'unit "' + unit.id + '" is not allowed for this element');
	}
	if (parsed.value < 0 && !measure.allowNegative) {
		throw fail('range', measure.label.toLowerCase() + ' cannot be negative');
	}
	// Fuel economy: 0 L/100km has no base, and 0 mpg no consumption.
	if (parsed.value === 0 && measure.allowZero === false) {
		throw fail('range', measure.label.toLowerCase() + ' must be more than zero');
	}

	const base = unit.toBase(parsed.value);
	if (!Number.isFinite(base)) throw fail('type', 'a measurement must be a finite number');

	// min/max are numbers in defaultUnit. A reciprocal default unit runs
	// against base, so its minimum is the largest base it allows: "at least
	// 4 L/100km" is "at most 25 km/L".
	const defaultUnit = defaultUnitId(measure, opts);
	const turned = getUnit(defaultUnit).decreasing === true;
	const min = boundInBase(opts.min, defaultUnit);
	const max = boundInBase(opts.max, defaultUnit);
	if (min !== null && (turned ? base > min : base < min)) {
		throw fail('range', 'must be at least ' + opts.min + ' ' + getUnit(defaultUnit).symbol);
	}
	if (max !== null && (turned ? base < max : base > max)) {
		throw fail('range', 'must be at most ' + opts.max + ' ' + getUnit(defaultUnit).symbol);
	}

	return { value: parsed.value, unit: unit.id, base };
}

/**
 * readMeasurement(raw, options) -> { value, unit, base } | null
 *
 * The tolerant read side: a stored object comes back as-is (with `base`
 * recomputed when missing), a bare number is a magnitude in the element's
 * default unit (a half-migrated `number` element), a string is parsed, and
 * anything else is null. Never throws.
 */
function readMeasurement(raw, options) {
	const opts = options || {};
	if (isMultiMeasure(opts)) return readSeveral(raw, opts);
	return readIn(raw, opts);
}

/**
 * A multi-measure element (KPR-1085): a stored value is read in its own
 * unit's measure, and one in a measure the element does not allow (left by a
 * removed measure's conversion that stopped part-way) is as unreadable as an
 * unknown unit -- no base. A number or text reads through the multi-measure
 * parser (a bare number is the primary's default unit).
 */
function readSeveral(raw, opts) {
	if (isMeasurementValue(raw)) {
		const scoped = scopeOptions(opts, measureOfUnit(raw.unit), raw.unit);
		if (!scoped) return { value: raw.value, unit: raw.unit, base: null };
		return readIn(raw, scoped);
	}
	if (isEmpty(raw)) return null;
	const parsed = parseMeasurement(raw, opts);
	if (!parsed) return null;
	const scoped = scopeOptions(opts, measureOfUnit(parsed.unit), parsed.unit);
	return scoped ? readIn(parsed, scoped) : null;
}

/**
 * One OWN unit's read side (KPR-1109): a stored value in that unit reads with
 * its base (the number itself when none is stored); one in any other unit has
 * none. A number or text reads as a number of the unit.
 */
function readOwn(raw, opts) {
	if (isMeasurementValue(raw)) {
		if (raw.unit !== opts.measure || !ownUnitOf(opts.measure, opts)) return { value: raw.value, unit: raw.unit, base: null };
		const base = typeof raw.base === 'number' && Number.isFinite(raw.base) ? raw.base : raw.value;
		return { value: raw.value, unit: raw.unit, base };
	}
	if (isEmpty(raw)) return null;
	const parsed = parseMeasurement(raw, opts);
	return parsed ? { value: parsed.value, unit: parsed.unit, base: parsed.value } : null;
}

/** The single-measure read side (the whole of it before KPR-1085). */
function readIn(raw, opts) {
	if (isOwnUnitId(opts.measure)) return readOwn(raw, opts);
	if (isMeasurementValue(raw)) {
		// A unit of another measure is as unreadable as an unknown one: no base,
		// rather than a length printed as a mass.
		const measure = getMeasure(opts.measure);
		const known = getUnit(raw.unit);
		const unit = known && measure && known.measure !== measure.id ? null : known;
		let base = unit && typeof raw.base === 'number' && Number.isFinite(raw.base) ? raw.base : null;
		if (base === null && unit) {
			const computed = unit.toBase(raw.value);
			base = Number.isFinite(computed) ? computed : null;
		}
		return { value: raw.value, unit: raw.unit, base };
	}
	if (isEmpty(raw)) return null;
	if (typeof raw === 'number' || typeof raw === 'string' || (raw && typeof raw === 'object')) {
		const parsed = parseMeasurement(raw, opts);
		if (!parsed) return null;
		const unit = getUnit(parsed.unit);
		const base = unit ? unit.toBase(parsed.value) : null;
		return { value: parsed.value, unit: parsed.unit, base: Number.isFinite(base) ? base : null };
	}
	return null;
}

module.exports = {
	normalizeMeasurement,
	readMeasurement,
};
